import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { OpsShift, OpsWeek, OpsPreference, OpsType, Team } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useOpsWeek, useOpsAction, useOpsPreferences, useSaveOpsPreferences, useOpsStats, useTeams } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { TeamChip } from '../components/RosterGrid.js';
import { Pill } from '../components/CoverBits.js';
import { emitError } from '../lib/toastBus.js';
import { dayLabel, timeRange, shortDate, addDays } from '../lib/shifts.js';

const TYPE_LABEL: Record<OpsType, string> = { weeknight: 'Weeknight', weekend_night: 'Weekend night', day: 'Day' };
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Ops support for a week: the support team takes shifts (one person each)
 * until the Thursday cut-off; then the team leader / deputy assigns what's
 * left. Fixed days and the team's load (last 12 months) are here too.
 */
export function OpsPage() {
  const back = useBack('/shifts');
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const week = params.get('week');
  const { data: w, isLoading, error } = useOpsWeek(orgId, week);

  const go = (ps: string) => setParams({ week: ps }, { replace: true });

  return (
    <PageShell noNav>
      <div style={{
        padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
      }}>
        <button onClick={back} aria-label="Back" style={iconBtn}>
          <Icon name="back" size={18} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Ops support</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {w && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => go(addDays(w.periodStart, -7))} aria-label="Previous week" style={navBtn}><Icon name="chevronL" size={16} /></button>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>Week of {shortDate(w.periodStart)}</div>
              <div style={{ fontSize: 12, color: theme.textMuted }}>{w.team ? <>Support team <TeamChip name={w.team.name} /></> : 'No support team rostered'}</div>
            </div>
            <button onClick={() => go(addDays(w.periodStart, 7))} aria-label="Next week" style={navBtn}><Icon name="chevron" size={16} /></button>
          </div>
        )}
        {isLoading && <div style={muted}>Loading…</div>}
        {error && <div style={muted}>{(error as Error).message}</div>}
        {w && <WeekView orgId={orgId} w={w} />}
        {w && <FixedDays orgId={orgId} />}
        {w?.team && <Load orgId={orgId} initialTeam={w.team.id} />}
      </div>
    </PageShell>
  );
}

function WeekView({ orgId, w }: { orgId: string; w: OpsWeek }) {
  const act = useOpsAction(orgId);
  const open = w.shifts.filter((s) => !s.userId && new Date(s.startsAt) > new Date()).length;
  const cutoff = new Date(w.cutoffAt).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric' });
  const run = (shiftId: string, action: 'claim' | 'release' | 'assign', userId?: string | null) =>
    act.mutate(action === 'assign' ? { shiftId, action, userId: userId ?? null } : { shiftId, action },
      { onError: (e) => emitError(e instanceof Error ? e.message : 'Something went wrong') });

  if (!w.shifts.length) return <div style={muted}>No ops-support shifts for this week yet.</div>;
  return (
    <Card pad={0}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${theme.border}`, fontSize: 13, color: theme.textDim, lineHeight: 1.45 }}>
        {w.pastCutoff
          ? <>The cut-off has passed. {w.canAssign ? 'Assign what’s left — a suggestion is shown for each.' : 'The team leader assigns what’s left.'} Can't make yours? Use <em>Can't make it</em> or <em>Swap</em> on My shifts.</>
          : <>One person per shift. {w.isMyTeam ? 'Take the ones that suit you' : 'The support team picks shifts'} by <strong>{cutoff}</strong>; after that the team leader assigns what's left.</>}
        {open > 0 && <div style={{ marginTop: 4 }}><Pill tone="warn">{open} still open</Pill></div>}
      </div>
      {w.shifts.map((s) => <ShiftRow key={s.id} s={s} w={w} busy={act.isPending} run={run} />)}
    </Card>
  );
}

function ShiftRow({ s, w, busy, run }: {
  s: OpsShift; w: OpsWeek; busy: boolean;
  run: (shiftId: string, action: 'claim' | 'release' | 'assign', userId?: string | null) => void;
}) {
  const past = new Date(s.startsAt) <= new Date();
  // the current person, else the suggestion
  const initial = s.userId ?? s.suggestion?.userId ?? '';
  const [pick, setPick] = useState<string>(initial);
  useEffect(() => { setPick(initial); }, [initial]);
  return (
    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${theme.border}`, opacity: past ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 96, flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{dayLabel(s.startsAt)}</div>
          <div style={{ fontSize: 12, color: theme.textMuted }}>{timeRange(s.startsAt, s.endsAt)}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: s.userId ? theme.text : theme.status.warn.fg }}>
            {s.userName ?? 'Open'}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted }}>{TYPE_LABEL[s.type]}{s.holiday ? ` · ${s.holiday}` : ''}</div>
        </div>
        {s.canClaim && <button disabled={busy} onClick={() => run(s.id, 'claim')} style={primarySm}>Take</button>}
        {s.canRelease && <button disabled={busy} onClick={() => run(s.id, 'release')} style={secondarySm}>Release</button>}
      </div>
      {w.canAssign && !past && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label={`Assign ${dayLabel(s.startsAt)}`} style={{ ...select, flex: '1 1 160px' }}>
            <option value="">— nobody</option>
            {w.members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName} · {m.counts[s.type]} {TYPE_LABEL[s.type].toLowerCase()}{m.counts[s.type] === 1 ? '' : 's'}{m.thisWeek ? ` · ${m.thisWeek} this wk` : ''}
              </option>
            ))}
          </select>
          <button disabled={busy || pick === (s.userId ?? '')} onClick={() => run(s.id, 'assign', pick || null)} style={secondarySm}>
            {pick ? 'Assign' : 'Clear'}
          </button>
          {!s.userId && s.suggestion && (
            <span style={{ fontSize: 11, color: theme.status.service.fg, flexBasis: '100%' }}>
              Suggested: {s.suggestion.displayName} — {s.suggestion.reason}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function FixedDays({ orgId }: { orgId: string }) {
  const { data: prefs = [] } = useOpsPreferences();
  const save = useSaveOpsPreferences(orgId);
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const key = (p: OpsPreference) => `${p.weekday}-${p.part}`;
  const current = draft ?? new Set(prefs.map(key));
  const changed = draft !== null && [...draft].sort().join() !== prefs.map(key).sort().join();
  const toggle = (k: string) => {
    const next = new Set(current);
    if (next.has(k)) next.delete(k); else next.add(k);
    setDraft(next);
  };
  const chip = (k: string, label: string) => (
    <button key={k} onClick={() => toggle(k)} aria-pressed={current.has(k)} style={{
      padding: '6px 0', width: 44, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
      border: `1px solid ${current.has(k) ? theme.accent : theme.border}`,
      background: current.has(k) ? theme.accent : theme.surface, color: current.has(k) ? theme.accentInk : theme.textDim,
    }}>{label}</button>
  );
  return (
    <Card>
      <div style={label}>My fixed days</div>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 8 }}>
        Filled in for you every week your team is on ops support. You can still release a week before the cut-off.
      </div>
      <div style={{ fontSize: 12, color: theme.textDim, marginBottom: 4 }}>Nights</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
        {DAYS.map((d, i) => chip(`${i + 1}-night`, d))}
      </div>
      <div style={{ fontSize: 12, color: theme.textDim, marginBottom: 4 }}>Days</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {chip('6-day', 'Sat')}{chip('7-day', 'Sun')}
      </div>
      {changed && (
        <button disabled={save.isPending} style={{ ...primarySm, marginTop: 10 }} onClick={() => {
          const list = [...current].map((k) => { const [d, p] = k.split('-'); return { weekday: Number(d), part: p as OpsPreference['part'] }; });
          save.mutate(list, { onSuccess: () => setDraft(null), onError: (e) => emitError(e instanceof Error ? e.message : 'Could not save') });
        }}>{save.isPending ? 'Saving…' : 'Save fixed days'}</button>
      )}
    </Card>
  );
}

function Load({ orgId, initialTeam }: { orgId: string; initialTeam: string }) {
  const { data: teams = [] } = useTeams(orgId);
  const [teamId, setTeamId] = useState(initialTeam);
  useEffect(() => { setTeamId(initialTeam); }, [initialTeam]);
  const { data: rows = [] } = useOpsStats(orgId, teamId);
  const th: React.CSSProperties = { padding: '6px 6px', fontSize: 11, fontWeight: 700, color: theme.textMuted, textAlign: 'right', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '6px 6px', fontSize: 13, color: theme.text, textAlign: 'right', borderTop: `1px solid ${theme.border}` };
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ ...label, marginBottom: 0, flex: 1 }}>Ops support done · last 12 months</span>
        <select value={teamId} onChange={(e) => setTeamId(e.target.value)} aria-label="Team" style={select}>
          {(teams as Team[]).filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: theme.font }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Member</th>
              <th style={th}>Week&shy;nights</th>
              <th style={th}>Weekend nights</th>
              <th style={th}>Days</th>
              <th style={th}>Covers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.userId}>
                <td style={{ ...td, textAlign: 'left' }}>{m.displayName}</td>
                <td style={td}>{m.counts.weeknight}</td>
                <td style={td}>{m.counts.weekend_night}</td>
                <td style={{ ...td, color: m.counts.day === 0 ? theme.status.warn.fg : theme.text, fontWeight: m.counts.day === 0 ? 700 : 400 }}>{m.counts.day}</td>
                <td style={td}>{m.counts.covers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>Day shifts are weekend and public-holiday days. Covers = covers taken for anyone, any shift.</div>
    </Card>
  );
}

function Card({ children, pad = 14 }: { children: React.ReactNode; pad?: number }) {
  return <div style={{ padding: pad ? `12px ${pad}px` : 0, borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}`, overflow: 'hidden', flexShrink: 0 }}>{children}</div>;
}

const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 6, display: 'block' };
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted, lineHeight: 1.5 };
const select: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13, borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface, color: theme.text, fontFamily: theme.font, minWidth: 0,
};
const primarySm: React.CSSProperties = {
  padding: '7px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, fontFamily: theme.font,
  background: theme.accent, color: theme.accentInk, cursor: 'pointer', flexShrink: 0,
};
const secondarySm: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 13, fontWeight: 700, fontFamily: theme.font,
  background: theme.surface, color: theme.text, cursor: 'pointer', flexShrink: 0,
};
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const navBtn: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
